import { useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import styled from "styled-components";
import { CODE_COUNTRY } from "../../common/constants";
import { reqEveryRate } from "../../requests/rate";

export default function Calculator() {
  const [countryCode, setCountryCode] = useState("");
  const [countryName, setCountryName] = useState("");
  const [destinationPostalCode, setDestinationPostalCode] = useState("");
  const [weight, setWeight] = useState(0);
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
    length: 0,
  });
  const [everyRate, setEveryRate] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 검색어에 따라 필터링된 국가 목록
  const filteredCountries = CODE_COUNTRY.filter(
    (country) =>
      country.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      country.code !== "GP"
  );

  // 드롭다운 외부 클릭 감지
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsCountryDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getEveryRate = async () => {
    setIsLoading(true);
    const res = await reqEveryRate({
      countryCode: countryCode,
      postalCode: destinationPostalCode || "",
      totalWeight: Number(weight) * 1000,
      boxWidth: dimensions.width,
      boxHeight: dimensions.height,
      boxLength: dimensions.length,
    });
    setIsLoading(false);
    setEveryRate(res.data);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    getEveryRate();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "width" || name === "height" || name === "length") {
      setDimensions((prevDimensions) => ({
        ...prevDimensions,
        [name]: parseFloat(value) || 0,
      }));
    } else if (name === "weight") {
      setWeight(parseFloat(value) || 0);
    }
  };

  const handleCountrySelect = (code: string, name: string) => {
    setCountryCode(code);
    setCountryName(name);
    setIsCountryDropdownOpen(false);
    setSearchQuery("");
  };

  return (
    <>
      <RateCalculator>
        {isLoading ? (
          <LoadingSection>
            <LoadingSpinner />
          </LoadingSection>
        ) : null}
        <CalculatorHeader>
          <h2>Shipping Rate Calculator</h2>
          <p>Compare shipping rates for different carriers</p>
        </CalculatorHeader>
        <RateForm onSubmit={handleSubmit}>
          <FormCard>
            <FormGroup>
              <Label htmlFor="countryCode">Recipient Country:</Label>
              <CountrySelectContainer ref={dropdownRef}>
                <CountrySelectInput
                  type="text"
                  value={countryName || searchQuery}
                  onClick={() => setIsCountryDropdownOpen(true)}
                  onChange={(e) => {
                    setCountryName("");
                    setCountryCode("");
                    setSearchQuery(e.target.value);
                    setIsCountryDropdownOpen(true);
                  }}
                  placeholder="Search for a country"
                  readOnly={!!countryName}
                />
                <SearchIcon>
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <path
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </SearchIcon>
                {countryName && (
                  <ClearButton
                    onClick={() => {
                      setCountryName("");
                      setCountryCode("");
                      setSearchQuery("");
                    }}
                  >
                    ✕
                  </ClearButton>
                )}
                {isCountryDropdownOpen && (
                  <CountryDropdown>
                    <SearchInputContainer>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search for a country"
                        autoFocus
                      />
                    </SearchInputContainer>
                    <CountryList>
                      {filteredCountries.length > 0 ? (
                        filteredCountries.map((country) => (
                          <CountryItem
                            key={country.code}
                            onClick={() =>
                              handleCountrySelect(country.code, country.name)
                            }
                          >
                            {country.name}
                          </CountryItem>
                        ))
                      ) : (
                        <NoResults>No results found</NoResults>
                      )}
                    </CountryList>
                  </CountryDropdown>
                )}
              </CountrySelectContainer>
            </FormGroup>
            <FormGroup>
              <Label htmlFor="destinationPostalCode">
                Destination Postal Code:
              </Label>
              <InputWithIcon>
                <FormControl
                  type="text"
                  id="destinationPostalCode"
                  value={destinationPostalCode}
                  onChange={(e) => setDestinationPostalCode(e.target.value)}
                  placeholder="Enter postal code"
                  required
                />
                <InputIcon>
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <path
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </InputIcon>
              </InputWithIcon>
            </FormGroup>
            <FormGroup>
              <Label htmlFor="weight">Package Weight (KG):</Label>
              <InputWithIcon>
                <FormControl
                  type="number"
                  id="weight"
                  name="weight"
                  value={weight.toString()}
                  onChange={(e) => setWeight(parseFloat(e.target.value) || 0)}
                  placeholder="Enter weight in kg"
                />
                <InputIcon>
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <path
                      d="M3 6l9-4 9 4v12l-9 4-9-4V6z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 22V12M3 10l9 4 9-4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </InputIcon>
              </InputWithIcon>
            </FormGroup>
            {isMobile ? (
              <>
                <FormGroup className="dimensions-group">
                  <Label>Package Dimensions (CM):</Label>
                  <DimensionsContainer>
                    <DimensionField>
                      <MLabel>Width:</MLabel>
                      <FormControl
                        type="number"
                        name="width"
                        value={dimensions.width.toString()}
                        onChange={handleChange}
                        placeholder="Width"
                        className="dimension-input"
                      />
                    </DimensionField>

                    <DimensionField>
                      <MLabel>Height:</MLabel>
                      <FormControl
                        type="number"
                        name="height"
                        value={dimensions.height.toString()}
                        onChange={handleChange}
                        placeholder="Height"
                        className="dimension-input"
                      />
                    </DimensionField>

                    <DimensionField>
                      <MLabel>Length:</MLabel>
                      <FormControl
                        type="number"
                        name="length"
                        value={dimensions.length.toString()}
                        onChange={handleChange}
                        placeholder="Length"
                        className="dimension-input"
                      />
                    </DimensionField>
                  </DimensionsContainer>
                </FormGroup>
              </>
            ) : (
              <FormGroup className="dimensions-group">
                <Label>Package Dimensions (CM):</Label>
                <DimensionsRow>
                  <DimensionField>
                    <MLabel>Width:</MLabel>
                    <FormControl
                      type="number"
                      name="width"
                      value={dimensions.width.toString()}
                      onChange={handleChange}
                      placeholder="Width"
                      className="dimension-input"
                    />
                  </DimensionField>

                  <DimensionField>
                    <MLabel>Height:</MLabel>
                    <FormControl
                      type="number"
                      name="height"
                      value={dimensions.height.toString()}
                      onChange={handleChange}
                      placeholder="Height"
                      className="dimension-input"
                    />
                  </DimensionField>

                  <DimensionField>
                    <MLabel>Length:</MLabel>
                    <FormControl
                      type="number"
                      name="length"
                      value={dimensions.length.toString()}
                      onChange={handleChange}
                      placeholder="Length"
                      className="dimension-input"
                    />
                  </DimensionField>
                </DimensionsRow>
              </FormGroup>
            )}

            <SubmitButton type="submit">
              <SubmitButtonContent>
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                  style={{ marginRight: "8px" }}
                >
                  <path
                    d="M9 7h6m0 10l-3-3m0 0l-3 3m3-3v-7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Calculate Shipping Rates
              </SubmitButtonContent>
            </SubmitButton>
          </FormCard>
        </RateForm>

        {everyRate && (
          <ResultsCard>
            <ResultsTitle>
              <ResultsIcon>
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                  <path
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </ResultsIcon>
              Shipping Rate Results
            </ResultsTitle>
            {isMobile ? (
              <>
                <RateItem>
                  <RateCarrier>FEDEX</RateCarrier>
                  <RateAmounts>
                    <RateAmount>
                      {everyRate.fedex?.krw || "-"} <small>KRW</small>
                    </RateAmount>
                    <RateAmount>
                      {everyRate.fedex?.usd || "-"} <small>USD</small>
                    </RateAmount>
                  </RateAmounts>
                </RateItem>
                <RateItem>
                  <RateCarrier>UPS</RateCarrier>
                  <RateAmounts>
                    <RateAmount>
                      {everyRate.ups?.krw || "-"} <small>KRW</small>
                    </RateAmount>
                    <RateAmount>
                      {everyRate.ups?.usd || "-"} <small>USD</small>
                    </RateAmount>
                  </RateAmounts>
                </RateItem>
                <RateItem>
                  <RateCarrier>EMS</RateCarrier>
                  <RateAmounts>
                    <RateAmount>
                      {everyRate.ems?.krw || "-"} <small>KRW</small>
                    </RateAmount>
                    <RateAmount>
                      {everyRate.ems?.usd || "-"} <small>USD</small>
                    </RateAmount>
                  </RateAmounts>
                </RateItem>
                <RateItem>
                  <RateCarrier>K-PACKET</RateCarrier>
                  <RateAmounts>
                    <RateAmount>
                      {everyRate.kpacket?.krw || "-"} <small>KRW</small>
                    </RateAmount>
                    <RateAmount>
                      {everyRate.kpacket?.usd || "-"} <small>USD</small>
                    </RateAmount>
                  </RateAmounts>
                </RateItem>
              </>
            ) : (
              <>
                <RateGrid>
                  <RateItem>
                    <RateCarrier>FEDEX</RateCarrier>
                    <RateAmounts>
                      <RateAmount>
                        {everyRate.fedex?.krw || "-"} <small>KRW</small>
                      </RateAmount>
                      <RateAmount>
                        {everyRate.fedex?.usd || "-"} <small>USD</small>
                      </RateAmount>
                    </RateAmounts>
                  </RateItem>
                  <RateItem>
                    <RateCarrier>UPS</RateCarrier>
                    <RateAmounts>
                      <RateAmount>
                        {everyRate.ups?.krw || "-"} <small>KRW</small>
                      </RateAmount>
                      <RateAmount>
                        {everyRate.ups?.usd || "-"} <small>USD</small>
                      </RateAmount>
                    </RateAmounts>
                  </RateItem>
                  <RateItem>
                    <RateCarrier>EMS</RateCarrier>
                    <RateAmounts>
                      <RateAmount>
                        {everyRate.ems?.krw || "-"} <small>KRW</small>
                      </RateAmount>
                      <RateAmount>
                        {everyRate.ems?.usd || "-"} <small>USD</small>
                      </RateAmount>
                    </RateAmounts>
                  </RateItem>
                  <RateItem>
                    <RateCarrier>K-PACKET</RateCarrier>
                    <RateAmounts>
                      <RateAmount>
                        {everyRate.kpacket?.krw || "-"} <small>KRW</small>
                      </RateAmount>
                      <RateAmount>
                        {everyRate.kpacket?.usd || "-"} <small>USD</small>
                      </RateAmount>
                    </RateAmounts>
                  </RateItem>
                </RateGrid>
              </>
            )}
          </ResultsCard>
        )}

        <InfoCard>
          <InfoTitle>
            <InfoIcon>
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                <path
                  d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20 10 10 0 010-20z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </InfoIcon>
            About Applied Weight
          </InfoTitle>
          <InfoText>
            <p>
              When making an estimate after packaging the product, use the{" "}
              <strong>applied weight</strong>.
            </p>
            <p>
              The <strong>applied weight</strong> is applied to a higher number
              of the <strong>actual weight</strong> and{" "}
              <strong>volume weight</strong>.
            </p>
            <p>
              The <strong>volume weight</strong> is calculated by calculating
              the (length * width * height) / 5000 of the box.
            </p>
            <InfoList>
              <InfoListItem>FEDEX, UPS measure / 5000</InfoListItem>
              <InfoListItem>EMS, K-PACKET measure / 6000</InfoListItem>
            </InfoList>
            <p>
              For example, if the <strong>actual weight</strong> of the box is
              12kg:
            </p>
            <p>
              If the box size is 60*50*30, the <strong>volume weight</strong> is
              calculated as 18kg (FEDEX, UPS), 15kg (EMS, K-PACKET).
            </p>
            <p>
              Therefore, the <strong>applied weight</strong> would be 18kg or
              15kg.
            </p>
          </InfoText>
        </InfoCard>
      </RateCalculator>
    </>
  );
}

const RateCalculator = styled.div`
  max-width: 1000px;
  margin: 2rem auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
`;

const CalculatorHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h2 {
    font-weight: 600;
    color: #262626;
    margin-bottom: 0.5rem;
    font-size: 24px;
  }

  p {
    color: #8e8e8e;
    font-size: 0.95rem;
  }
`;

const RateForm = styled.form`
  width: 100%;
`;

const FormCard = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: rgba(0, 0, 0, 0.04) 0px 0px 5px, rgba(0, 0, 0, 0.05) 0px 1px 2px;
  padding: 24px;
  margin-bottom: 20px;
  border: 1px solid #efefef;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: rgba(0, 0, 0, 0.08) 0px 4px 12px;
  }
`;

const ResultsCard = styled(FormCard)`
  padding: 20px;
`;

const InfoCard = styled(FormCard)`
  padding: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #262626;
  font-size: 14px;
`;

const MLabel = styled.label`
  font-weight: 600;
  margin-right: 8px;
  color: #262626;
  font-size: 14px;
  min-width: 50px;
  display: inline-block;
`;

const InputWithIcon = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #8e8e8e;
  pointer-events: none;
`;

const FormControl = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #dbdbdb;
  border-radius: 6px;
  font-size: 14px;
  transition: border 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #8e8e8e;
    box-shadow: rgba(0, 0, 0, 0.05) 0px 0px 0px 1px;
  }

  &::placeholder {
    color: #8e8e8e;
  }
`;

const CountrySelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const CountrySelectInput = styled.input`
  width: 100%;
  padding: 12px;
  padding-right: 60px;
  border: 1px solid #dbdbdb;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  background-color: white;
  transition: border 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #8e8e8e;
    box-shadow: rgba(0, 0, 0, 0.05) 0px 0px 0px 1px;
  }

  &::placeholder {
    color: #8e8e8e;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #8e8e8e;
  pointer-events: none;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 32px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 16px;
  color: #8e8e8e;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;

  &:hover {
    color: #262626;
  }
`;

const CountryDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 300px;
  background: white;
  border-radius: 6px;
  margin-top: 4px;
  box-shadow: rgba(0, 0, 0, 0.11) 0px 4px 12px;
  z-index: 100;
  border: 1px solid #efefef;
  overflow: hidden;
`;

const SearchInputContainer = styled.div`
  padding: 8px;
  border-bottom: 1px solid #efefef;

  input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #dbdbdb;
    border-radius: 4px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: #8e8e8e;
    }

    &::placeholder {
      color: #8e8e8e;
    }
  }
`;

const CountryList = styled.div`
  max-height: 240px;
  overflow-y: auto;

  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #c7c7c7;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const CountryItem = styled.div`
  padding: 10px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 14px;

  &:hover {
    background-color: #fafafa;
  }
`;

const NoResults = styled.div`
  padding: 16px;
  text-align: center;
  color: #8e8e8e;
  font-size: 14px;
`;

const DimensionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DimensionsRow = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const DimensionField = styled.div`
  display: flex;
  align-items: center;
  flex: 1;

  ${FormControl} {
    flex: 1;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px;
  background: linear-gradient(
    to right,
    #5851db,
    #833ab4,
    #c13584,
    #e1306c,
    #fd1d1d,
    #f56040
  );
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }
`;

const SubmitButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingSection = styled.div`
  position: fixed;
  z-index: 1000;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: conic-gradient(#0000 10%, #c13584);
  -webkit-mask: radial-gradient(farthest-side, #0000 calc(100% - 8px), #000 0);
  animation: rotate 1s infinite linear;

  @keyframes rotate {
    to {
      transform: rotate(1turn);
    }
  }
`;

const ResultsTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ResultsIcon = styled.span`
  margin-right: 8px;
  display: inline-flex;
  color: #262626;
`;

const InfoTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InfoIcon = styled.span`
  margin-right: 8px;
  display: inline-flex;
  color: #262626;
`;

const InfoText = styled.div`
  color: #262626;
  font-size: 14px;
  line-height: 1.5;

  p {
    margin-bottom: 12px;
  }

  strong {
    font-weight: 600;
  }
`;

const InfoList = styled.ul`
  margin: 12px 0;
  padding-left: 16px;
`;

const InfoListItem = styled.li`
  margin-bottom: 8px;
  position: relative;
  padding-left: 8px;

  &::before {
    content: "";
    position: absolute;
    left: -8px;
    top: 8px;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: #8e8e8e;
  }
`;

const RateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RateItem = styled.div`
  background-color: #fafafa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid #efefef;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: rgba(0, 0, 0, 0.05) 0px 4px 12px;
  }
`;

const RateCarrier = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #262626;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #efefef;
`;

const RateAmounts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const RateAmount = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #262626;

  small {
    font-size: 12px;
    color: #8e8e8e;
    font-weight: normal;
  }
`;
